import { useState } from "react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar, Siren, Zap, Trophy } from "lucide-react";
import { useTickerFallbackData, type TickerFallbackData } from "@/hooks/useTickerFallbackData";

interface EditorialFallbackRailProps {
  nextFixtureDate: string | null;
}

interface FallbackCard {
  id: string;
  badge: string;
  badgeColor: string;
  icon: React.ReactNode;
  text: string;
  subtext: string;
  logos?: { home: string; away: string };
  leagueLogos?: string[];
}

const SEPARATOR = (
  <span className="text-[hsl(0_0%_30%)] text-[10px] mx-2 flex-shrink-0 select-none" aria-hidden>
    ◆
  </span>
);

function buildCards(data: TickerFallbackData): FallbackCard[] {
  const cards: FallbackCard[] = [];

  if (data.nextMatch) {
    const m = data.nextMatch;
    const matchDay = format(new Date(m.match_date), "EEEE");
    const matchTime = format(new Date(m.match_date), "HH:mm");
    cards.push({
      id: "next-match",
      badge: "NEXT BIG MATCH",
      badgeColor: "bg-[hsl(45_90%_35%)] text-[hsl(0_0%_8%)]",
      icon: <Trophy className="w-3 h-3" />,
      text: `${m.home_team} vs ${m.away_team}`,
      subtext: `${matchDay} · ${matchTime} · SG7 preview available soon`,
      logos: { home: m.home_logo, away: m.away_logo },
      leagueLogos: m.league_logo ? [m.league_logo] : [],
    });
  }

  if (data.gapHours !== null && data.nextMatch) {
    const days = Math.ceil(data.gapHours / 24);
    const returnDay = format(new Date(data.nextMatch.match_date), "EEEE");
    const allLeagues =
      data.weekGames.length > 0
        ? data.weekGames
            .flatMap((wg) => wg.leagues)
            .filter((v, i, a) => a.findIndex((x) => x.name === v.name) === i)
            .slice(0, 3)
        : [];
    const leagueList = allLeagues.length > 0
      ? allLeagues.map((l) => l.name).join(", ")
      : "Top European leagues";

    const badgeText =
      days <= 0
        ? "FOOTBALL RETURNS TODAY"
        : days === 1
        ? "FOOTBALL RETURNS TOMORROW"
        : `MATCHDAY RETURNS IN ${days} DAYS`;

    cards.push({
      id: "countdown",
      badge: badgeText,
      badgeColor: "bg-[hsl(142_40%_20%)] text-[hsl(142_70%_55%)]",
      icon: <Calendar className="w-3 h-3" />,
      text: leagueList + " and more",
      subtext: `Coverage resumes ${returnDay}`,
      leagueLogos: allLeagues.map((l) => l.logo).filter(Boolean),
    });
  }

  if (data.insightStats && data.insightStats.teamsAlerted > 0) {
    const s = data.insightStats;
    cards.push({
      id: "insight-perf",
      badge: "SG7 ALERTS",
      badgeColor: "bg-[hsl(0_80%_30%)] text-[hsl(0_90%_75%)]",
      icon: <Siren className="w-3 h-3" />,
      text: `${s.teamsAlerted} alerted teams - ${s.teamsScored} scored - ${s.successRate}% Success Rate`,
      subtext: "Last matchday · Live Radar intelligence",
    });
  }

  if (data.weekGames.length > 0) {
    const firstDay = data.weekGames[0];
    const leaguePreview = firstDay.leagues.slice(0, 2).map((l) => l.name).join(", ");
    cards.push({
      id: "week-agenda",
      badge: "COMING UP THIS WEEK",
      badgeColor: "bg-[hsl(0_0%_20%)] text-[hsl(0_0%_70%)]",
      icon: <Zap className="w-3 h-3" />,
      text: `${firstDay.day}: ${leaguePreview} returns`,
      subtext: "Live Radar · Picks · Match Analysis",
      leagueLogos: firstDay.leagues.slice(0, 2).map((l) => l.logo).filter(Boolean),
    });
  }

  return cards;
}

function FallbackCardView({ card }: { card: FallbackCard }) {
  return (
    <div className="inline-flex items-center gap-2 px-1 py-1 whitespace-nowrap flex-shrink-0">
      <span
        className={cn(
          "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider",
          card.badgeColor
        )}
      >
        {card.icon}
        {card.badge}
      </span>
      {card.leagueLogos && card.leagueLogos.length > 0 && (
        <span className="inline-flex items-center gap-0.5">
          {card.leagueLogos.map((logo, i) => (
            <img key={i} src={logo} alt="" className="w-3.5 h-3.5 object-contain" loading="lazy" />
          ))}
        </span>
      )}
      {card.logos && (
        <span className="inline-flex items-center gap-1">
          {card.logos.home && (
            <img src={card.logos.home} alt="" className="w-3.5 h-3.5 object-contain" loading="lazy" />
          )}
        </span>
      )}
      <span className="text-[12px] font-bold text-[hsl(0_0%_92%)]">{card.text}</span>
      {card.logos && card.logos.away && (
        <img src={card.logos.away} alt="" className="w-3.5 h-3.5 object-contain" loading="lazy" />
      )}
      <span className="text-[hsl(0_0%_50%)] text-[10px] flex-shrink-0 select-none" aria-hidden>
        •
      </span>
      <span className="text-[11px] text-[hsl(0_0%_55%)]">{card.subtext}</span>
    </div>
  );
}

export function EditorialFallbackRail({ nextFixtureDate }: EditorialFallbackRailProps) {
  const { data } = useTickerFallbackData(true, nextFixtureDate);
  const [isPaused, setIsPaused] = useState(false);

  const cards = data ? buildCards(data) : [];

  if (!data || cards.length === 0) {
    return (
      <div className="sticky top-0 z-30 bg-[hsl(var(--sg7-surface-1))] border-b border-[hsl(var(--sg7-stroke-subtle))]">
        <div className="h-10 flex items-center justify-center">
          <span className="text-[11px] text-[hsl(0_0%_40%)] font-medium uppercase tracking-wider">
            SG7 · No fixtures scheduled
          </span>
        </div>
      </div>
    );
  }

  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1400;
  const estItemWidth = 300;
  const singleSetWidth = cards.length * estItemWidth;
  const minCopies = Math.max(2, Math.ceil((viewportWidth * 2) / singleSetWidth) + 1);
  const duration = Math.max(singleSetWidth / 35, 20);
  const loopOffset = `-${(100 / minCopies).toFixed(4)}%`;

  const renderCards = (keyPrefix: string) =>
    cards.map((card, i) => (
      <span key={`${keyPrefix}-${card.id}-${i}`} className="inline-flex items-center">
        {i > 0 && SEPARATOR}
        <FallbackCardView card={card} />
      </span>
    ));

  return (
    <div
      className="sticky top-0 z-30 bg-[hsl(var(--sg7-surface-1))] border-b border-[hsl(var(--sg7-stroke-subtle))] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[hsl(var(--sg7-surface-1))] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[hsl(var(--sg7-surface-1))] to-transparent z-10 pointer-events-none" />

      <div
        className="flex items-center h-10 ticker-scroll-container"
        style={{
          "--ticker-duration": `${duration}s`,
          "--ticker-loop-offset": loopOffset,
          animationPlayState: isPaused ? "paused" : "running",
        } as React.CSSProperties}
      >
        {Array.from({ length: minCopies }, (_, copyIdx) => (
          <div key={`copy-${copyIdx}`} className="inline-flex items-center flex-nowrap">
            {copyIdx > 0 && SEPARATOR}
            {renderCards(`c${copyIdx}`)}
          </div>
        ))}
      </div>
    </div>
  );
}
